import { useEffect, useMemo, useState } from "react";
import { useOpportunities } from "@/hooks/useOpportunities";
import { deriveDaysAgo } from "@/utils/opportunityDerived";

/**
 * Intelligence Brief — compact, restrained session context block that
 * lives in the upper-left of the hero (empty in RTL) without pulling
 * attention away from the headline.
 *
 * Data contract:
 *   • Greeting          → time-of-day, name fallback per spec.
 *   • Arabic date       → Arabic weekday + month, English day/year.
 *   • Session summary   → real data (today's new opportunities + total
 *                         under monitoring). We don't have per-user
 *                         session deltas (no auth yet) so we surface
 *                         the safest live database metrics.
 *   • Last update       → current GST (Asia/Dubai) time.
 *
 * Visual rules (per spec):
 *   • No background, no border, no shadow, no icons.
 *   • Muted navy/gray text.
 *   • Emerald only for meaningful numeric highlights.
 *   • English numerals throughout.
 *   • Balanced Arabic wrapping.
 */

const AR_WEEKDAYS = [
  "الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت",
];
const AR_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

const getGreeting = (date, userName) => {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Dubai",
      hour: "2-digit",
      hour12: false,
    }).format(date),
  );
  const time = hour >= 5 && hour < 12 ? "صباح الخير" : "مساء الخير";
  return userName ? `${time}، ${userName}` : `${time}، مرحباً بك`;
};

const formatArabicDate = (date) => {
  // Parts in Asia/Dubai timezone so travellers see the local date.
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).formatToParts(date);
  const jsDate = new Date(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Dubai",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date).replace(/-/g, "/"),
  );
  const weekday = AR_WEEKDAYS[jsDate.getDay()];
  const day     = parts.find((p) => p.type === "day")?.value ?? "";
  const monthEn = parts.find((p) => p.type === "month")?.value ?? "";
  const year    = parts.find((p) => p.type === "year")?.value ?? "";
  const monthIdx = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ].indexOf(monthEn);
  const month = monthIdx >= 0 ? AR_MONTHS[monthIdx] : monthEn;
  return `${weekday}، ${day} ${month} ${year}`;
};

const formatGstTime = (date) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);

export const IntelligenceBrief = ({ className = "", userName = null, compact = false }) => {
  const { opportunities } = useOpportunities();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Tick every minute so the GST clock stays honest without flooding rAF.
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const greeting  = useMemo(() => getGreeting(now, userName), [now, userName]);
  const dateLabel = useMemo(() => formatArabicDate(now), [now]);
  const gstTime   = useMemo(() => formatGstTime(now), [now]);

  // Real database metrics — safest fall-back from spec.
  const todayCount  = opportunities.filter((o) => deriveDaysAgo(o.publishedAt) === 0).length;
  const activeCount = opportunities.length;
  const hasSessionMetrics = todayCount > 0 || activeCount > 0;

  if (compact) {
    return (
      <aside
        data-testid="hero-intelligence-brief-compact"
        aria-label="ملخّص الجلسة"
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] leading-tight text-ink-500 ${className}`}
      >
        <span data-testid="brief-greeting-compact" className="font-semibold text-ink-900">
          {greeting}
        </span>
        <span aria-hidden className="text-surface-300">·</span>
        <span data-testid="brief-date-compact">{dateLabel}</span>
        <span aria-hidden className="text-surface-300">·</span>
        <span data-testid="brief-last-update-compact" className="whitespace-nowrap">
          آخر تحديث{" "}
          <span dir="ltr" className="num font-mono">
            {gstTime}
          </span>{" "}
          GST
        </span>
      </aside>
    );
  }

  return (
    <aside
      data-testid="hero-intelligence-brief"
      aria-label="ملخّص الجلسة"
      className={`text-right ${className}`}
    >
      <p
        data-testid="brief-greeting"
        className="arabic-body text-[13px] font-semibold leading-snug text-ink-900 text-balance"
      >
        {greeting}
      </p>
      <p
        data-testid="brief-date"
        className="mt-1 text-[12px] leading-snug text-ink-500"
      >
        {dateLabel}
      </p>

      {hasSessionMetrics && (
        <p
          data-testid="brief-session"
          className="mt-3 text-[12px] leading-relaxed text-ink-700 text-pretty"
        >
          اليوم{" "}
          <span dir="ltr" className="num font-mono font-semibold text-fursa-700">
            {todayCount}
          </span>{" "}
          فرصة جديدة
          <span aria-hidden className="mx-1.5 text-surface-300">·</span>
          <span dir="ltr" className="num font-mono font-semibold text-fursa-700">
            {activeCount}
          </span>{" "}
          فرصة تحت المتابعة
        </p>
      )}

      <p
        data-testid="brief-last-update"
        className="mt-2 text-[10.5px] font-medium uppercase tracking-widest text-ink-500"
      >
        آخر تحديث{" "}
        <span dir="ltr" className="num font-mono">
          {gstTime}
        </span>{" "}
        GST
      </p>
    </aside>
  );
};
